import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import {
  searchReportingFetch as fetch,
  searchReportingLegacyUrl,
} from '../lib/flags/search-reporting';
import { getAuthToken } from '../utils/auth';

// Types for merge operation
export interface MergeProgress {
  status: 'idle' | 'fetching_related_data' | 'confirming' | 'merging' | 'completed' | 'failed';
  currentStep: string;
  totalSteps: number;
  completedSteps: number;
  paymentsCount: number;
  subscriptionsCount: number;
  processedPayments: number;
  processedSubscriptions: number;
  error?: string;
  mergeJobId?: string;
  startTime?: number;
  primaryContactId?: string;
  secondaryContactId?: string;
  primaryContactName?: string;
  secondaryContactName?: string;
}

export interface MergeData {
  primaryContactId: string;
  secondaryContactId: string;
  primaryContactName: string;
  secondaryContactName: string;
  mergeSelections: {
    firstName: 'contact1' | 'contact2' | null;
    lastName: 'contact1' | 'contact2' | null;
    organizationName: 'contact1' | 'contact2' | null;
    emails: 'contact1' | 'contact2' | 'both' | null;
    phones: 'contact1' | 'contact2' | 'both' | null;
    address: 'contact1' | 'contact2' | 'both' | null;
  };
  primarySelections: {
    primaryEmailIndex: number | null;
    primaryPhoneIndex: number | null;
    primaryAddressIndex: number | null;
  };
  mergedContactData: {
    firstName: string;
    lastName: string;
    organizationName: string;
    emails: Array<{ value: string; is_primary: boolean }>;
    phones: Array<{ value: string; is_primary: boolean }>;
    addresses: Array<{
      line1?: string;
      line2?: string;
      city?: string;
      region?: string;
      postal?: string;
      country?: string;
      is_primary: boolean;
    }>;
  };
}

export interface RelatedDataCounts {
  paymentsCount: number;
  subscriptionsCount: number;
  balanceTransactionsCount: number;
  formRecordsCount: number;
  totalValue: number;
}

interface MergeContextType {
  // State
  progress: MergeProgress;
  isMergeInProgress: boolean;
  mergeData: MergeData | null;
  relatedDataCounts: RelatedDataCounts | null;

  // Actions
  startMerge: (data: MergeData, jwtToken: string, merchantName: string) => Promise<void>;
  fetchRelatedData: (
    secondaryContactId: string,
    jwtToken: string,
    merchantName: string,
  ) => Promise<RelatedDataCounts>;
  resetMerge: () => void;
  pollMergeStatus: (jobId: string, jwtToken: string) => void;
  checkInProgressMerge: (jwtToken: string, merchantName: string) => Promise<void>;
}

const initialProgress: MergeProgress = {
  status: 'idle',
  currentStep: '',
  totalSteps: 0,
  completedSteps: 0,
  paymentsCount: 0,
  subscriptionsCount: 0,
  processedPayments: 0,
  processedSubscriptions: 0,
};

// Mapping of merge phases to completed step counts
// Order: payments (1) → subscriptions (2) → balance_transactions (3)
const MERGE_PHASE_STEPS: Record<string, number> = {
  payments: 1,
  subscriptions: 2,
  balance_transactions: 3,
};

const MergeContext = createContext<MergeContextType | undefined>(undefined);

export function MergeProvider({ children }: { children: React.ReactNode }) {
  const [progress, setProgress] = useState<MergeProgress>(initialProgress);
  const [mergeData, setMergeData] = useState<MergeData | null>(null);
  const [relatedDataCounts, setRelatedDataCounts] = useState<RelatedDataCounts | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isMergeInProgress =
    progress.status === 'merging' || progress.status === 'fetching_related_data';

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const fetchRelatedData = useCallback(
    async (
      secondaryContactId: string,
      jwtToken: string,
      merchantName: string,
    ): Promise<RelatedDataCounts> => {
      setProgress((prev) => ({
        ...prev,
        status: 'fetching_related_data',
        currentStep: 'Fetching related data...',
      }));

      try {
        const opensearchUrl = searchReportingLegacyUrl;

        // Fetch payments count for the secondary contact
        // Use 'transactions' query with SUCCEEDED state filter to match how contact totals are aggregated
        const paymentsResponse = await fetch(`${opensearchUrl}/transactions`, {
          method: 'POST',
          headers: {
            accept: '*/*',
            'content-type': 'application/json',
            Authorization: await getAuthToken(),
          },
          body: JSON.stringify({
            query: 'transactions',
            filters: {
              merchantNames: [merchantName],
              contactIDs: [secondaryContactId],
              states: ['SUCCEEDED', 'PARTIAL_REFUND'],
              limit: 1, // We only need the count from aggregations, not the actual results
            },
          }),
        });

        let paymentsCount = 0;
        let totalValue = 0;
        if (paymentsResponse.ok) {
          const paymentsData = await paymentsResponse.json();
          // The transactions query returns an array with totalCount and totalAggregationAmount on the first result
          if (Array.isArray(paymentsData) && paymentsData.length > 0) {
            paymentsCount = paymentsData[0]?.totalCount || 0;
            totalValue = paymentsData[0]?.totalAggregationAmount || 0;
          }
        }

        // Fetch subscriptions count for the secondary contact
        // Use 'subscriptions' query with contactIDs filter
        const subscriptionsResponse = await fetch(`${opensearchUrl}/subscriptions`, {
          method: 'POST',
          headers: {
            accept: '*/*',
            'content-type': 'application/json',
            Authorization: await getAuthToken(),
          },
          body: JSON.stringify({
            query: 'subscriptions',
            filters: {
              merchantNames: [merchantName],
              contactIDs: [secondaryContactId],
              limit: 1, // We only need the count from aggregations, not the actual results
            },
          }),
        });

        let subscriptionsCount = 0;
        if (subscriptionsResponse.ok) {
          const subscriptionsData = await subscriptionsResponse.json();
          // The subscriptions query returns an array with totalCount on the first result
          if (Array.isArray(subscriptionsData) && subscriptionsData.length > 0) {
            subscriptionsCount = subscriptionsData[0]?.totalCount || 0;
          }
        }

        const counts: RelatedDataCounts = {
          paymentsCount,
          subscriptionsCount,
          balanceTransactionsCount: 0, // Will be fetched from backend
          formRecordsCount: 0, // Will be fetched from backend
          totalValue,
        };

        setRelatedDataCounts(counts);
        setProgress((prev) => ({
          ...prev,
          status: 'idle',
          paymentsCount,
          subscriptionsCount,
        }));

        return counts;
      } catch (error) {
        console.error('Error fetching related data:', error);
        setProgress((prev) => ({
          ...prev,
          status: 'idle',
          error: 'Failed to fetch related data',
        }));
        throw error;
      }
    },
    [],
  );

  const pollMergeStatus = useCallback((jobId: string, jwtToken: string) => {
    const contactsUrl = import.meta.env.VITE_APP_DASHBOARD_CONTACTS_URL;

    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    const poll = async () => {
      try {
        const response = await fetch(`${contactsUrl}/contacts/merge/status/${jobId}`, {
          method: 'GET',
          headers: {
            accept: '*/*',
            'content-type': 'application/json',
            Authorization: await getAuthToken(),
          },
        });

        if (response.ok) {
          const data = await response.json();

          setProgress((prev) => ({
            ...prev,
            currentStep: data.currentStep || prev.currentStep,
            completedSteps: data.completedSteps || prev.completedSteps,
            totalSteps: data.totalSteps || prev.totalSteps,
            processedPayments: data.processedPayments || prev.processedPayments,
            processedSubscriptions: data.processedSubscriptions || prev.processedSubscriptions,
            status:
              data.status === 'completed'
                ? 'completed'
                : data.status === 'failed'
                ? 'failed'
                : 'merging',
            error: data.error,
          }));

          // Stop polling if completed or failed
          if (data.status === 'completed' || data.status === 'failed') {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        }
      } catch (error) {
        console.error('Error polling merge status:', error);
      }
    };

    // Poll immediately, then every 2 seconds
    poll();
    pollingIntervalRef.current = setInterval(poll, 2000);
  }, []);

  const startMerge = useCallback(
    async (data: MergeData, jwtToken: string, merchantName: string) => {
      setMergeData(data);
      setProgress({
        status: 'merging',
        currentStep: 'Initiating merge...',
        totalSteps: 5,
        completedSteps: 0,
        paymentsCount: relatedDataCounts?.paymentsCount || 0,
        subscriptionsCount: relatedDataCounts?.subscriptionsCount || 0,
        processedPayments: 0,
        processedSubscriptions: 0,
        startTime: Date.now(),
        primaryContactId: data.primaryContactId,
        secondaryContactId: data.secondaryContactId,
        primaryContactName: data.primaryContactName,
        secondaryContactName: data.secondaryContactName,
      });
      // Note: Progress modal is no longer auto-opened - toast notifications are used instead

      try {
        const contactsUrl = import.meta.env.VITE_APP_DASHBOARD_CONTACTS_URL;

        const response = await fetch(`${contactsUrl}/contacts/merge`, {
          method: 'POST',
          headers: {
            accept: '*/*',
            'content-type': 'application/json',
            Authorization: await getAuthToken(),
          },
          body: JSON.stringify({
            primaryContactId: data.primaryContactId,
            secondaryContactId: data.secondaryContactId,
            merchantName,
            mergedContactData: data.mergedContactData,
          }),
        });

        if (response.ok) {
          const result = await response.json();
          const jobId = result.jobId;
          const status = result.status;

          setProgress((prev) => ({
            ...prev,
            mergeJobId: jobId,
            currentStep: result.message || 'Processing merge...',
          }));

          // Check if merge completed synchronously (status is 'completed' or 'completed_with_warnings')
          if (status === 'completed' || status === 'completed_with_warnings') {
            // Merge completed synchronously - no need to poll
            setProgress((prev) => ({
              ...prev,
              status: 'completed',
              currentStep: result.message || 'Merge completed successfully!',
              completedSteps: prev.totalSteps,
              processedPayments: result.totalProcessed || 0,
              processedSubscriptions: 0,
            }));
          } else if (status === 'processing' && jobId) {
            // Merge is still processing (async/continuation) - would need polling endpoint
            // For now, just mark as completed after a delay since we don't have a status endpoint
            setProgress((prev) => ({
              ...prev,
              currentStep: 'Merge in progress...',
            }));
            // Since we don't have a status endpoint, wait a bit and mark as completed
            setTimeout(() => {
              setProgress((prev) => ({
                ...prev,
                status: 'completed',
                currentStep: 'Merge completed!',
                completedSteps: prev.totalSteps,
              }));
            }, 3000);
          } else {
            // Unknown status, mark as completed
            setProgress((prev) => ({
              ...prev,
              status: 'completed',
              currentStep: 'Merge completed!',
              completedSteps: prev.totalSteps,
            }));
          }
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || errorData.error || 'Failed to start merge');
        }
      } catch (error) {
        console.error('Error starting merge:', error);
        setProgress((prev) => ({
          ...prev,
          status: 'failed',
          error: error instanceof Error ? error.message : 'Failed to start merge',
          currentStep: 'Merge failed',
        }));
      }
    },
    [relatedDataCounts, pollMergeStatus],
  );

  const resetMerge = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setProgress(initialProgress);
    setMergeData(null);
    setRelatedDataCounts(null);
  }, []);

  const checkInProgressMerge = useCallback(
    async (jwtToken: string, merchantName: string) => {
      try {
        const contactsUrl = import.meta.env.VITE_APP_DASHBOARD_CONTACTS_URL;
        const response = await fetch(
          `${contactsUrl}/contacts/merge/in-progress?merchantName=${encodeURIComponent(
            merchantName,
          )}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: await getAuthToken(),
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          if (data.inProgress && data.merge) {
            // Found an in-progress merge - update state to reflect it
            const checkpoint = data.merge.checkpoint || {};
            setProgress({
              status: 'merging',
              currentStep: `Merge in progress (${checkpoint.phase || 'processing'}...)`,
              totalSteps: 5,
              completedSteps: MERGE_PHASE_STEPS[checkpoint.phase] ?? 0,
              paymentsCount: checkpoint.total_processed || 0,
              subscriptionsCount: 0,
              processedPayments: checkpoint.total_success || 0,
              processedSubscriptions: 0,
              mergeJobId: data.merge.mergeJobId,
              startTime: data.merge.startedAt
                ? new Date(data.merge.startedAt).getTime()
                : Date.now(),
              primaryContactId: data.merge.primaryContactId,
              secondaryContactId: data.merge.secondaryContactId,
              primaryContactName: data.merge.primaryContactName,
              secondaryContactName: data.merge.secondaryContactName,
            });

            // Start polling for status updates so UI reflects when merge completes
            pollMergeStatus(data.merge.mergeJobId, jwtToken);
          }
        }
      } catch (error) {
        console.error('Error checking for in-progress merge:', error);
        // Don't throw - this is a background check, we don't want to break the UI
      }
    },
    [pollMergeStatus],
  );

  return (
    <MergeContext.Provider
      value={{
        progress,
        isMergeInProgress,
        mergeData,
        relatedDataCounts,
        startMerge,
        fetchRelatedData,
        resetMerge,
        pollMergeStatus,
        checkInProgressMerge,
      }}
    >
      {children}
    </MergeContext.Provider>
  );
}

export function useMerge() {
  const context = useContext(MergeContext);
  if (context === undefined) {
    throw new Error('useMerge must be used within a MergeProvider');
  }
  return context;
}
